import type { ReactNode } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import type { ConfigApi } from '@eis/insuranceservice-sdk';

/**
 * What the gate said (service summary §3.4).
 *
 * The gate does not answer yes or no — it answers with a REPORT, and the difference matters most
 * exactly when it refuses. A repriced golden case is a decision for an actuary: they need the
 * case, the number that was expected and the number that came out. "Validation failed" gives
 * them nothing, so this panel renders the triples as a table and never collapses them into prose.
 *
 * Expected and actual are decimal STRINGS all the way from the service, and they stay strings
 * here. A premium is money; formatting it through a float would be the console introducing the
 * error the contract went out of its way to avoid.
 */
export function GateReportPanel({
  report,
  refused,
}: {
  readonly report: ConfigApi.GateReport;
  /** True when this report came back with a refusal rather than from a passing dry run. */
  readonly refused: boolean;
}): ReactNode {
  const { t } = useTranslation('insurance');
  const deltas = report.premiumDeltas ?? [];
  const contractIssues = report.contractIssues ?? [];
  const smokeFailures = report.smokeFailures ?? [];
  const passed = report.passed === true && !refused;
  const reviewNotes = report.reviewNotes ?? [];
  const assigned = report.assignedNumber ?? '';

  return (
    <Stack spacing={2} data-testid="gate-report">
      <Alert severity={passed ? 'success' : 'error'}>
        <AlertTitle>
          {passed ? t('tarifs.publish.gate.passed') : t('tarifs.publish.gate.failed')}
        </AlertTitle>
        {passed ? t('tarifs.publish.gate.passedHint') : t('tarifs.publish.gate.failedHint')}
      </Alert>

      {/* The gate NUMBERS the version (SDD-016 D-V2): the author reads the verdict, never sets it. */}
      {passed && assigned !== '' && (
        <Alert severity="info">
          {t('tarifs.publish.gate.assignedNumber', { number: assigned })}{' '}
          {deltas.length > 0 ? t('tarifs.publish.gate.assignedMajor') : null}
        </Alert>
      )}

      {/* Findings the four-eyes review must see and that refuse nothing (SDD-016 §8). */}
      {reviewNotes.length > 0 && (
        <Alert severity="warning">
          <AlertTitle>{t('tarifs.publish.gate.reviewNotes')}</AlertTitle>
          <Stack component="ul" spacing={0.5} sx={{ m: 0, pl: 2 }}>
            {reviewNotes.map((note) => (
              <li key={`${note.path ?? ''}-${note.code ?? ''}`}>{note.message}</li>
            ))}
          </Stack>
        </Alert>
      )}

      {deltas.length > 0 && (
        <Box>
          <Typography
            variant="subtitle2"
            component="span"
            id="gate-premium-deltas"
            sx={{ display: 'block', mb: 1 }}
          >
            {t('tarifs.publish.gate.premiumDeltas')}
          </Typography>
          <Table size="small" aria-labelledby="gate-premium-deltas">
            <TableHead>
              <TableRow>
                <TableCell>{t('tarifs.publish.gate.goldenCase')}</TableCell>
                <TableCell align="right">{t('tarifs.publish.gate.expected')}</TableCell>
                <TableCell align="right">{t('tarifs.publish.gate.actual')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deltas.map((delta) => (
                <TableRow key={delta.goldenCaseId}>
                  <TableCell>{delta.goldenCaseId}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                    {delta.expected}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                    {delta.actual}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {contractIssues.length > 0 && (
        <Box>
          <Typography
            variant="subtitle2"
            component="span"
            id="gate-contract-issues"
            sx={{ display: 'block', mb: 1 }}
          >
            {t('tarifs.publish.gate.contractIssues')}
          </Typography>
          <Box component="ul" aria-labelledby="gate-contract-issues" sx={{ m: 0, pl: 3 }}>
            {contractIssues.map((issue) => (
              <Typography component="li" variant="body2" key={`${issue.path}${issue.code}`}>
                <Box component="code" sx={{ fontFamily: 'monospace' }}>
                  {issue.path}
                </Box>{' '}
                {issue.message}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {smokeFailures.length > 0 && (
        <Box>
          <Typography
            variant="subtitle2"
            component="span"
            id="gate-smoke"
            sx={{ display: 'block', mb: 1 }}
          >
            {t('tarifs.publish.gate.smokeFailures')}
          </Typography>
          <Box component="ul" aria-labelledby="gate-smoke" sx={{ m: 0, pl: 3 }}>
            {smokeFailures.map((failure) => (
              <Typography component="li" variant="body2" key={failure}>
                {failure}
              </Typography>
            ))}
          </Box>
        </Box>
      )}
    </Stack>
  );
}
